// CoachHost — the coaching / guided-tour impure-shell host, extracted from
// AppShellHost (the composition root) so the onboarding feature owns its own
// file instead of colliding on the shared hot file (split-the-hot-file,
// .claude/analysis/dev-orchestration-model.md §10a). Mirrors useEditorHost /
// useNavHost: the shell threads its deps in through ONE named `CoachHostDeps`
// and this hook hands back the ready tour `overlay` element (the GuidedTour),
// the coaching model's `isFreshForAuthor` signal (which the hero surface also
// reads, so the shell relays it), the derived `arcProminence` (Dm4), the
// `requestGuidedTour` intent (the landing's "Take a guided tour" click, D28),
// and a `resetCoaching` action for the dev reset-onboarding command.
// Pure-shell discipline: the tour's step state, its one persistence write
// (markTourSeen) and the getBoundingClientRect MEASUREMENT live here; the
// GuidedTour component is pure. `heroActive` is the cross-wire with the hero
// surface — the shell computes it once and passes it into both hosts.

import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import {
    type ActivationProgress,
    type ArcProminence,
    arcProminence,
    authorExperience,
    type OnboardingSurface,
    onboardingSurface,
    thisAuthor,
    type WorkspaceStatus,
    workspaceStatus,
} from "../../app/coaching.js";
import { removeDeviceKey } from "../../app/deviceStore.js";
import type { Library } from "../../app/ports.js";
import { markTourSeen } from "../../app/tourSeen.js";
import {
    GuidedTour,
    type TargetRect,
    type Tour,
    type TourLayout,
    type TourPhase,
    type TourTargetId,
} from "../GuidedTour/GuidedTour.js";
import type { RecentThread } from "../WorkspaceLanding/WorkspaceLanding.js";

// How the guided tour finds each REAL spotlight target in the DOM. The composer
// and its "+" reuse the landing's own composer class (the landing marks its
// composer there; PowerComposer renders the "+" in its leading slot); the rail
// declares a stable `data-tour-target` on its root. Host-only — used by
// `measureTourTargets` (a sanctioned DOM read).
const TOUR_TARGET_SELECTORS: Record<TourTargetId, string> = {
    attach: ".bilda-workspace-landing__composer .bilda-composer-power__leading",
    composer: ".bilda-workspace-landing__composer",
    rail: '[data-tour-target="rail"]',
};
const TOUR_TARGET_IDS: readonly TourTargetId[] = ["attach", "composer", "rail"];
// Snapshot each present target's viewport rect (getBoundingClientRect is an
// impure MEASUREMENT — called only from the host's step effect). A missing
// element is simply skipped, so an absent target degrades to a centred popup
// rather than a crash.
const measureTourTargets = (): Partial<Record<TourTargetId, TargetRect>> => {
    const rects: Partial<Record<TourTargetId, TargetRect>> = {};
    for (const id of TOUR_TARGET_IDS) {
        const el = document.querySelector(TOUR_TARGET_SELECTORS[id]);
        if (el === null) continue;
        const r = el.getBoundingClientRect();
        rects[id] = {
            top: r.top,
            left: r.left,
            width: r.width,
            height: r.height,
        };
    }
    return rects;
};
// One snapshot of everything the tour's placement needs: the targets, the
// window (clientWidth/Height, so a scrollbar is excluded) and the popup's own
// rendered size (offset* ignores transforms).
const measureTourLayout = (popup: HTMLDivElement | null): TourLayout => ({
    rects: measureTourTargets(),
    viewport: {
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight,
    },
    popup:
        popup === null
            ? null
            : { width: popup.offsetWidth, height: popup.offsetHeight },
});

// The dependency surface the coaching host pulls from the shell. `heroActive`
// is the hero cross-wire (the tour is home-only chrome); `library` +
// `conversationSummaries` feed the per-author WorkspaceStatus; `tipsDismissed`
// is the coaching model's Advanced-ladder signal.
export type CoachHostDeps = {
    readonly heroActive: boolean;
    readonly library: Library | null;
    readonly conversationSummaries: readonly RecentThread[];
    readonly tipsDismissed: boolean;
};

export type CoachHostApi = {
    // The coaching model's per-author "fresh workspace" signal — the hero
    // surface's landing state keys on it too, so the shell relays this value
    // into HeroHost to keep the two surfaces in step.
    readonly isFreshForAuthor: boolean;
    // The ready tour overlay (the GuidedTour). Renders nothing at rest (idle).
    readonly overlay: ReactNode;
    // WHICH first-run CENTRE surface a coached newcomer should see — the pure
    // (experience × status × progress) decision (app/coaching.ts). `none` at
    // rest. The shell routes each case to its component in the correct centre
    // slot; the placement stays the shell's.
    readonly onboardingSurface: OnboardingSurface;
    // How loudly the hero outcome arc should present itself — derived from the
    // `AuthorExperience` ladder (app/coaching.ts), no separate toggle. Relayed
    // for the hero surface to thread into its arc.
    readonly arcProminence: ArcProminence;
    // Start the guided tour from its first step — the landing's "Take a guided
    // tour" click (D28). The tour never starts on its own (Dm9).
    readonly requestGuidedTour: () => void;
    // Clear the device-local tour marker and close any open tour (dev
    // reset-onboarding). Own intent, own method — the shell cannot reach the
    // host's private state otherwise.
    readonly resetCoaching: () => void;
};

export const useCoachHost = (deps: CoachHostDeps): CoachHostApi => {
    const { t } = useLingui();
    const { heroActive, library, conversationSummaries, tipsDismissed } = deps;

    const [tourPhase, setTourPhase] = useState<TourPhase>({ type: "idle" });
    const [tourLayout, setTourLayout] = useState<TourLayout | null>(null);
    const tourPopupRef = useRef<HTMLDivElement>(null);
    // The current author's PICTURE of the workspace — the WorkspaceStatus axis
    // (per-workspace, domain-derived; app/coaching.ts, IDD f95f4542), NOT a shell
    // heuristic. `Fresh` = this author sees no resources or conversations yet.
    // Reads are already author-scoped (single-tenant MVP), so the footprint is
    // `thisAuthor`'s. `library === null` (not yet loaded) yields `null` —
    // deliberately NOT fresh, so nothing flashes before the read lands.
    // `lastActivityAt: null` keeps the Stale split off until a clock is wired.
    const authorWorkspace: WorkspaceStatus | null =
        library === null
            ? null
            : workspaceStatus(thisAuthor, {
                  resourceCount:
                      library.sources.length +
                      library.materials.length +
                      library.exports.length,
                  conversationCount: conversationSummaries.length,
                  lastActivityAt: null,
              });
    const isFreshForAuthor = authorWorkspace?.type === "fresh";
    // The author's EXPERIENCE ladder (device-local axis; IDD f95f4542), derived
    // from a real dismissal affordance ("stäng av tips" → Advanced).
    const experience = authorExperience({ tipsDismissed });
    const prominence = arcProminence(experience);

    // The author's ACTIVATION PROGRESS — a pure projection of the Library, NOT a
    // stored onboarding flag (deleting the last source flips `hasSource` back).
    const progress: ActivationProgress =
        library === null
            ? { hasSource: false, hasMaterial: false, hasPackage: false }
            : {
                  hasSource: library.sources.length > 0,
                  hasMaterial: library.materials.length > 0,
                  hasPackage: library.exports.length > 0,
              };
    const surface: OnboardingSurface =
        authorWorkspace === null
            ? { type: "none" }
            : onboardingSurface(experience, authorWorkspace, progress);

    // The guided tour (D28): the landing's three ideas, each spotlit on the
    // real thing it is about — the "+" (add sources), the composer (describe
    // what to create), and the rail (where conversations and the Library live;
    // review before export). Built with `t` so the copy is extractable; the
    // pure component takes it as a prop.
    const guidedTour: Tour = {
        steps: [
            {
                target: "attach",
                placement: "bottom",
                title: t`Add sources`,
                body: t`Use + to add a chapter, your notes or a plan. The assistant works from your sources.`,
            },
            {
                target: "composer",
                placement: "bottom",
                title: t`Describe what you want to create`,
                body: t`Write what, for whom, and what they should be able to do afterwards.`,
            },
            {
                target: "rail",
                placement: "right",
                title: t`Review and export`,
                body: t`Your conversations are listed here, and what you create ends up in the library. You review it before anything is exported.`,
            },
        ],
    };

    const requestGuidedTour = () => setTourPhase({ type: "step", index: 0 });
    // Every terminal route (×, scrim-click, the last step's "OK") ends the same
    // way: stamp "seen" (device-local lifecycle marker, kept for a future
    // recap; nothing reads it today) and go idle.
    const endTour = () => {
        markTourSeen();
        setTourPhase({ type: "idle" });
    };
    const onTourBack = () =>
        setTourPhase((p) =>
            p.type === "step" && p.index > 0
                ? { type: "step", index: p.index - 1 }
                : p,
        );
    const lastTourStep = guidedTour.steps.length - 1;
    const onTourNext = () =>
        setTourPhase((p) =>
            p.type === "step" && p.index < lastTourStep
                ? { type: "step", index: p.index + 1 }
                : p,
        );

    // The tour is home-only chrome: present `idle` while the author is off-home
    // so nothing lingers over a resource.
    const effectiveTourPhase: TourPhase = heroActive
        ? tourPhase
        : { type: "idle" };
    // The step whose target must be measured (-1 when nothing is showing) — the
    // remeasure effect's re-run signal.
    const tourStepIndex =
        effectiveTourPhase.type === "step" ? effectiveTourPhase.index : -1;

    // Measure the REAL spotlight targets, the window and the popup at the
    // impure host edge (sanctioned layout MEASUREMENTS, not buried side
    // effects) whenever a step is showing, and re-measure on resize/scroll and
    // whenever the popup's size changes, so the ring stays aligned and the
    // popup stays inside the window. A layout effect so the first frame is
    // already placed. The pure component never touches the DOM.
    useLayoutEffect(() => {
        if (tourStepIndex < 0) return;
        const remeasure = () =>
            setTourLayout(measureTourLayout(tourPopupRef.current));
        remeasure();
        window.addEventListener("resize", remeasure);
        window.addEventListener("scroll", remeasure, true);
        const popup = tourPopupRef.current;
        const observer =
            popup === null || typeof ResizeObserver === "undefined"
                ? null
                : new ResizeObserver(remeasure);
        if (popup !== null) observer?.observe(popup);
        return () => {
            window.removeEventListener("resize", remeasure);
            window.removeEventListener("scroll", remeasure, true);
            observer?.disconnect();
        };
    }, [tourStepIndex]);

    // Dev reset-onboarding: clear the device-local tour marker and close any
    // open tour. The shell clears its own `tipsDismissed` alongside this call.
    const resetCoaching = () => {
        removeDeviceKey("bilda.tourSeen");
        setTourPhase({ type: "idle" });
    };

    const overlay: ReactNode = (
        <GuidedTour
            tour={guidedTour}
            phase={effectiveTourPhase}
            layout={tourLayout}
            popupRef={tourPopupRef}
            onBack={onTourBack}
            onNext={onTourNext}
            onSkip={endTour}
        />
    );

    return {
        isFreshForAuthor,
        overlay,
        resetCoaching,
        onboardingSurface: surface,
        arcProminence: prominence,
        requestGuidedTour,
    };
};
